<template>
  <section class="card text-revisions" aria-label="Text revisions" v-if="target">
    <div class="card-header"><h4 class="card-title">Text revisions — {{ target.lang.toUpperCase() }}</h4></div>
    <div class="card-body">
      <p>Save and check the text first, then pin a named version of the saved content. Unsaved editor changes are not included.</p>
      <div v-if="aliases.length" class="mb-16">
        <label :for="scopeInputId">Version scope</label>
        <el-select :id="scopeInputId" v-model="fieldAlias" :disabled="creating || busy" placeholder="All saved fields">
          <el-option label="All saved fields" value="" />
          <el-option v-for="alias in aliases" :key="alias" :label="alias" :value="alias" />
        </el-select>
      </div>
      <p v-if="fieldAlias">This pins only {{ fieldAlias }} in {{ target.lang.toUpperCase() }}.</p>
      <label :for="inputId">Revision name</label>
      <el-input :id="inputId" v-model="label" placeholder="e.g. Before SEO update" :maxlength="120" :disabled="creating" />
      <base-button type="primary" class="mt-16" :disabled="busy || creating || !label.trim()" @click="createRevision">
        {{ creating ? 'Creating revision…' : 'Create revision' }}
      </base-button>
      <base-button class="mt-16 ml-16" :disabled="loading || creating || busy" @click="load">Refresh revisions</base-button>
      <p v-if="error" role="alert" class="text-danger mt-16">{{ error }}</p>
      <p v-if="success" role="status" class="text-success mt-16">{{ success }}</p>
      <h5 class="mt-16">Last 10 revisions</h5>
      <p v-if="loading">Loading revisions…</p>
      <p v-else-if="!error && !revisions.length">No revisions yet.</p>
      <table v-if="revisions.length" class="table">
        <thead><tr><th>Revision</th><th>Field / scope</th><th>Name</th><th>Date</th><th>Action</th></tr></thead>
        <tbody><tr v-for="revision in revisions" :key="revision.id">
          <td>#{{ revision.id }}</td><td>{{ revision.target && revision.target.alias || "All fields" }}</td><td>{{ revision.label || revision.operation }}</td><td>{{ revision.created_at }}</td>
          <td><base-button size="sm" :disabled="viewing || busy" @click="view(revision)">View</base-button></td>
        </tr></tbody>
      </table>
      <el-dialog title="Saved revision" :visible.sync="dialogOpen" width="85%">
        <template v-if="selected">
          <h4>#{{ selected.id }} — {{ selected.label || selected.operation }}</h4>
          <p>{{ selected.created_at }} · {{ selected.target.lang.toUpperCase() }}</p>
          <p v-if="!selectedFields.length">This revision has no saved text fields.</p>
          <div v-for="field in selectedFields" :key="field.alias">
            <h5>{{ field.alias }}</h5><pre class="revision-html">{{ field.exists === false ? 'Field did not exist' : field.text }}</pre>
          </div>
        </template>
      </el-dialog>
    </div>
  </section>
</template>

<script>
import Revisions from '@/api/modules/text-revisions';

export default {
  props: { target: { type: Object, default: null }, aliases: { type: Array, default: () => [] }, busy: { type: Boolean, default: false } },
  data: () => ({ fieldAlias: '', label: '', revisions: [], loading: false, creating: false, viewing: false, error: '', success: '', selected: null, dialogOpen: false, generation: 0, readSequence: 0, pending: null }),
  computed: {
    baseTargetKey() { return JSON.stringify(this.target); },
    scopedTarget() {
      if (!this.target || !this.fieldAlias) return this.target;
      if (!this.aliases.includes(this.fieldAlias)) return null;
      return { ...this.target, kind: this.target.kind.replace('_set', ''), alias: this.fieldAlias };
    },
    scopeInputId() { return `text-revision-scope-${this._uid}`; },
    selectedFields() { if (!this.selected) return []; const state = this.selected.state; return Array.isArray(state.texts) ? state.texts : [{ alias: this.selected.target.alias, ...state }]; }, targetKey() { return JSON.stringify(this.scopedTarget); }, inputId() { return `text-revision-name-${this._uid}`; } },
  watch: {
    baseTargetKey() { this.fieldAlias = ''; },
    busy(value, old) { if (old && !value) this.load(); },
    targetKey: { immediate: true, handler() {
      this.generation++;
      this.label = ''; this.revisions = []; this.selected = null; this.dialogOpen = false;
      this.error = ''; this.success = ''; this.pending = null; this.creating = false; this.viewing = false;
      this.load();
    } },
  },
  beforeDestroy() { this.generation++; this.readSequence++; },
  methods: {
    async load() {
      const generation = this.generation, sequence = ++this.readSequence;
      if (!this.scopedTarget) { this.loading = false; return; }
      this.loading = true;
      try {
        const response = await Revisions.list({ ...this.scopedTarget });
        if (generation !== this.generation || sequence !== this.readSequence) return;
        if (!Array.isArray(response.data.revisions)) throw Error('invalid response');
        this.revisions = response.data.revisions;
        this.error = '';
      } catch (error) {
        if (generation === this.generation && sequence === this.readSequence) this.error = 'Could not load revisions. Refresh to try again.';
      } finally {
        if (generation === this.generation && sequence === this.readSequence) this.loading = false;
      }
    },
    async createRevision() {
      if (!this.scopedTarget || this.busy || this.creating || !this.label.trim()) return;
      const generation = this.generation, label = this.label.trim(), key = this.targetKey;
      if (!this.pending || this.pending.key !== key || this.pending.label !== label) {
        const bytes = new Uint32Array(4);
        window.crypto.getRandomValues(bytes);
        this.pending = { key, label, id: `cms-${Array.from(bytes, n => n.toString(16)).join('-')}` };
      }
      const pending = this.pending;
      this.creating = true; this.error = ''; this.success = '';
      try {
        const response = await Revisions.create({ ...this.scopedTarget }, label, pending.id);
        if (generation !== this.generation) return;
        if (!response.data.id) throw Error('invalid response');
        this.pending = null; this.label = '';
        this.success = `Revision #${response.data.id} “${label}” created from saved texts.`;
        await this.load();
      } catch (error) {
        if (generation === this.generation) this.error = 'Revision was not confirmed. Try the same name again to safely retry.';
      } finally {
        if (generation === this.generation) this.creating = false;
      }
    },
    async view(revision) {
      const generation = this.generation;
      this.viewing = true; this.error = '';
      try {
        const response = await Revisions.get(revision.id);
        if (generation !== this.generation) return;
        const saved = response.data.target, scope = this.scopedTarget;
        const matchingSet = JSON.stringify(saved) === this.targetKey;
        const matchingField = saved && saved.kind === scope.kind.replace('_set', '') &&
          Object.keys(scope).filter(key => key !== 'kind').every(key => saved[key] === scope[key]);
        if ((!matchingSet && !matchingField) || !response.data.state ||
            (!Array.isArray(response.data.state.texts) && typeof response.data.state.exists !== 'boolean')) throw Error('wrong revision context');
        this.selected = response.data; this.dialogOpen = true;
      } catch (error) {
        if (generation === this.generation) this.error = 'Could not open this revision.';
      } finally {
        if (generation === this.generation) this.viewing = false;
      }
    },
  },
};
</script>

<style scoped>
.revision-html { white-space: pre-wrap; overflow-wrap: anywhere; max-height: 320px; overflow: auto; }
</style>
